import { z } from 'zod';
import { acquisitionTypeSchema, fuelSchema, inventoryStatusSchema, originSchema, profitModeSchema, serviceHistorySchema, usageSchema, } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { amountSchema, isoDateSchema, revisionSchema, signedMoneySchema } from '../../common/schemas/money.schemas.js';
import { paginatedSchema, paginationInputSchema, sortDirectionSchema } from '../../common/schemas/pagination.schemas.js';
import { approvalSummarySchema } from '../../approvals/schemas/approvals.schemas.js';
import { costItemsSummarySchema } from '../../costs/schemas/costs.schemas.js';
import { documentsSummarySchema } from '../../documents/schemas/documents.schemas.js';
import { activityEventSchema, contactSummarySchema, estimateValuationSchema, inspectionSchema, modelYearSchema, odometerSchema, ownerCountSchema, paramOverridesInputSchema, policyRefSchema, templateRefSchema, valuationSnapshotSummarySchema, vehicleFactsSchema, vinSchema, } from '../../estimates/schemas/estimates.schemas.js';
import { tradeInCaseSchema } from '../../trade-in/schemas/trade-in.schemas.js';
/**
 * A **stock vehicle**: one purchased episode, and the only kind of inventory this platform has
 * (plan §3.1). There is no "add vehicle" anywhere — a row here exists because `estimates.purchase`
 * created it, which is why every vehicle can name the estimate and the negotiation behind it.
 *
 * It repeats the estimate's fact columns exactly (`vehicleFactsSchema`), so the purchase copies them
 * 1:1, and adds what only a purchased vehicle has: the stock number, what was actually paid and when,
 * where it is standing, what CTA is asking for it, and — from phase 6 — how it was sold.
 */
export const vehicleSourceEstimateSchema = z.object({
    id: uuidSchema,
    estimateNo: z.string(),
    customerName: z.string().nullable(),
});
export const vehicleSummarySchema = z.object({
    id: uuidSchema,
    stockNo: z.string(),
    status: inventoryStatusSchema,
    make: z.string().nullable(),
    model: z.string().nullable(),
    modelYear: z.number().int().nullable(),
    odometerKm: z.number().int().nullable(),
    vin: z.string().nullable(),
    purchasePrice: amountSchema.nullable(),
    purchaseDate: isoDateSchema.nullable(),
    askingPrice: amountSchema.nullable(),
    salePrice: amountSchema.nullable(),
    saleDate: isoDateSchema.nullable(),
    /** Purchase date → today, or → the sale date once sold. `null` while no purchase date is recorded. */
    daysInStock: z.number().int().min(0).nullable(),
    storageLocation: z.string().nullable(),
    templateId: uuidSchema.nullable(),
    templateName: z.string().nullable(),
    assignedUserId: uuidSchema.nullable(),
    assignedUserName: z.string().nullable(),
    sourceEstimateId: uuidSchema.nullable(),
    sourceEstimateNo: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * One inventory move the vehicle could make, with the reasons it currently cannot (plan §5.6).
 *
 * The server answers this on every read so the dialog can offer only what is possible **and say why** a
 * target is refused, instead of letting somebody choose it and read a 400 in front of a customer. The
 * blockers are reason keys the UI translates.
 */
export const vehicleTransitionSchema = z.object({
    to: inventoryStatusSchema,
    allowed: z.boolean(),
    blockers: z.array(z.string()),
});
/** What the sale recorded, for the "Completed sale" card and the dossier header (plan §7). */
export const vehicleSaleSchema = z.object({
    salePrice: amountSchema.nullable(),
    saleDate: isoDateSchema.nullable(),
    buyer: contactSummarySchema.nullable(),
    soldBelowRetail: z.boolean(),
    saleNote: z.string().nullable(),
    /**
     * The required retail the equations worked out **at the moment of the sale**, taken from the `SALE`
     * snapshot. It is the figure the below-retail rule was checked against, so the card can show the
     * comparison a person was actually held to rather than today's projection.
     */
    requiredRetailAtSale: z.number().nullable(),
});
/**
 * `sale_price − projected_total_cost` of the latest snapshot (PP §3: completed-sale gross contribution).
 * `provisional` repeats that snapshot's flag: while any cost is still an estimate, the contribution is
 * an indication and not a settled figure.
 */
export const realizedContributionSchema = z.object({
    amount: signedMoneySchema,
    provisional: z.boolean(),
});
/**
 * One administrator's correction of a closed record (plan §5.6; PP §4: "append a reasoned correction
 * event, preserve the original transaction"). The approval row carries the decision and the reason; the
 * activity row carries the old and new values.
 */
export const vehicleCorrectionSchema = z.object({
    id: uuidSchema,
    field: z.string(),
    from: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    to: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    reason: z.string().nullable(),
    costItemId: uuidSchema.nullable(),
    byUserId: uuidSchema.nullable(),
    byUserName: z.string().nullable(),
    at: z.string().datetime(),
});
export const vehicleDetailSchema = vehicleFactsSchema.extend({
    id: uuidSchema,
    stockNo: z.string(),
    status: inventoryStatusSchema,
    revision: revisionSchema,
    sourceEstimate: vehicleSourceEstimateSchema.nullable(),
    previousVehicleId: uuidSchema.nullable(),
    storageLocation: z.string().nullable(),
    purchasePrice: amountSchema.nullable(),
    purchaseDate: isoDateSchema.nullable(),
    sellerContactId: uuidSchema.nullable(),
    seller: contactSummarySchema.nullable(),
    askingPrice: amountSchema.nullable(),
    buyerContactId: uuidSchema.nullable(),
    buyer: contactSummarySchema.nullable(),
    salePrice: amountSchema.nullable(),
    saleDate: isoDateSchema.nullable(),
    soldBelowRetail: z.boolean(),
    saleNote: z.string().nullable(),
    template: templateRefSchema.nullable(),
    policy: policyRefSchema.nullable(),
    inspection: inspectionSchema.nullable(),
    valuation: estimateValuationSchema,
    latestSnapshot: valuationSnapshotSummarySchema.nullable(),
    /** The fingerprint of the vehicle *as it is now* (plan §5.2). */
    currentFingerprint: z.string(),
    costItems: costItemsSummarySchema,
    /** What is on file, for the dossier's tab badge and the photo-set line. Informational (plan §3.1). */
    documents: documentsSummarySchema,
    tradeIn: tradeInCaseSchema.nullable(),
    approval: approvalSummarySchema.nullable(),
    /**
     * Days from the purchase date to today, in the tenant's calendar — and **frozen at the sale date**
     * once the vehicle is sold, because a sold car stops ageing (plan §5.2; PP §3).
     */
    daysInStock: z.number().int().min(0).nullable(),
    /** Every target this vehicle could move to, with the blockers the rule reports (plan §5.6). */
    availableTransitions: z.array(vehicleTransitionSchema),
    /** `null` until the vehicle is sold. */
    sale: vehicleSaleSchema.nullable(),
    /** `null` until the vehicle is sold. */
    realizedContribution: realizedContributionSchema.nullable(),
    /** Administrator corrections, newest first. Empty for a vehicle nobody has had to correct. */
    corrections: z.array(vehicleCorrectionSchema),
    assignedUserId: uuidSchema.nullable(),
    assignedUserName: z.string().nullable(),
    createdBy: uuidSchema.nullable(),
    activityCount: z.number().int().min(0),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const listVehiclesSortBySchema = z.enum(['purchaseDate', 'stockNo', 'updatedAt', 'purchasePrice', 'askingPrice', 'salePrice', 'daysInStock']);
export const listVehiclesInputSchema = paginationInputSchema.extend({
    sortBy: listVehiclesSortBySchema.default('purchaseDate'),
    sortDirection: sortDirectionSchema.default('desc'),
    /** Free text over the stock number, the VIN, the make and the model. */
    q: z.string().trim().min(1).optional(),
    status: z.array(inventoryStatusSchema).optional(),
    templateId: uuidSchema.optional(),
    assignedUserId: uuidSchema.optional(),
    purchaseDateFrom: isoDateSchema.optional(),
    purchaseDateTo: isoDateSchema.optional(),
    saleDateFrom: isoDateSchema.optional(),
    saleDateTo: isoDateSchema.optional(),
});
export const listVehiclesOutputSchema = paginatedSchema(vehicleSummarySchema);
export const getVehicleInputSchema = z.object({
    vehicleId: uuidSchema,
});
/**
 * PATCH semantics with `expectedRevision`. `changeStatus` and `correct` are **phase 6**: an open vehicle's
 * facts, inspection, overrides, costs, asking price, storage location and assignee are editable here, and
 * a SOLD vehicle refuses every one of them (plan §5.6).
 */
export const updateVehicleInputSchema = z.object({
    vehicleId: uuidSchema,
    expectedRevision: revisionSchema,
    make: z.string().trim().max(80).nullish(),
    model: z.string().trim().max(80).nullish(),
    trim: z.string().trim().max(80).nullish(),
    modelYear: modelYearSchema.nullish(),
    vin: vinSchema.nullish(),
    plate: z.string().trim().max(32).nullish(),
    drivetrain: z.string().trim().max(32).nullish(),
    transmission: z.string().trim().max(32).nullish(),
    fuel: fuelSchema.optional(),
    color: z.string().trim().max(40).nullish(),
    origin: originSchema.nullish(),
    usage: usageSchema.nullish(),
    odometerKm: odometerSchema.nullish(),
    ownerCount: ownerCountSchema.nullish(),
    registrationExpiry: isoDateSchema.nullish(),
    firstRegistrationDate: isoDateSchema.nullish(),
    referencePrice: amountSchema.nullish(),
    referenceSource: z.string().trim().max(255).nullish(),
    referenceDate: isoDateSchema.nullish(),
    faceliftApplies: z.boolean().nullish(),
    customsApplies: z.boolean().nullish(),
    hasSpareKey: z.boolean().optional(),
    hasOwnersManual: z.boolean().optional(),
    hasToolKit: z.boolean().optional(),
    hasSpareTire: z.boolean().optional(),
    hasWarranty: z.boolean().optional(),
    serviceHistory: serviceHistorySchema.optional(),
    equipment: z.array(z.string().trim().min(1).max(120)).max(100).optional(),
    acquisitionType: acquisitionTypeSchema.optional(),
    profitMode: profitModeSchema.optional(),
    /** Only keys the template version marks overridable are accepted; anything else is a 400. */
    paramOverrides: paramOverridesInputSchema.optional(),
    taxTrafficEstimate: amountSchema.nullish(),
    otherDirectCostEstimate: amountSchema.nullish(),
    storageLocation: z.string().trim().max(200).nullish(),
    askingPrice: amountSchema.nullish(),
    sellerContactId: uuidSchema.nullish(),
    assignedUserId: uuidSchema.nullish(),
});
/**
 * The buyer of a sold vehicle: an existing contact, or a name and mobile the service finds or creates by
 * **normalised phone** — the same mechanism the estimator uses for the customer, and with the same
 * promise: an existing contact is linked as it stands and is never renamed (plan §11 item 11).
 */
export const saleBuyerSchema = z.object({
    name: z.string().trim().min(1).max(200),
    phone: z.string().trim().min(5).max(40),
});
export const recordSaleSchema = z
    .object({
    salePrice: amountSchema.positive(),
    saleDate: isoDateSchema,
    buyerContactId: uuidSchema.optional(),
    buyer: saleBuyerSchema.optional(),
    /** Required when the price is below the required retail: an administrator has to say why. */
    note: z.string().trim().max(2_000).optional(),
})
    .refine((sale) => sale.buyerContactId !== undefined || sale.buyer !== undefined, {
    message: 'Record who bought the vehicle',
    path: ['buyer'],
});
/**
 * `IN_WAREHOUSE ⇄ IN_SERVICE → SOLD` (plan §5.6).
 *
 * A move between the warehouse and the workshop carries a note — "why is this car not where it was?" is
 * the question the log exists to answer. A sale carries the price, the date and the buyer, and a price
 * below the required retail is an **administrator's** decision with a reason.
 */
export const changeVehicleStatusInputSchema = z.object({
    vehicleId: uuidSchema,
    expectedRevision: revisionSchema,
    to: inventoryStatusSchema,
    note: z.string().trim().max(2_000).optional(),
    sale: recordSaleSchema.optional(),
});
/** The columns an administrator may correct on a closed record (plan §5.6). */
export const vehicleCorrectionFieldSchema = z.enum([
    'purchasePrice',
    'purchaseDate',
    'salePrice',
    'saleDate',
    'buyerContactId',
    'sellerContactId',
    'askingPrice',
    'storageLocation',
    'costItemActual',
]);
/**
 * One correction, with a reason (plan §5.6; PP §4). It never edits history in place: the old value stays
 * in the activity row, the decision stays in an `approvals` row of type `CORRECTION`, and a money field
 * writes a fresh `CORRECTION` snapshot so the derived reporting moves with it.
 */
export const correctVehicleInputSchema = z.object({
    vehicleId: uuidSchema,
    expectedRevision: revisionSchema,
    field: vehicleCorrectionFieldSchema,
    /** Typed per field: an amount, a `YYYY-MM-DD` date, a contact id or a line of text. `null` clears. */
    newValue: z.union([z.string(), z.number(), z.null()]),
    /** Required for `costItemActual`: which recorded cost the amount belongs to. */
    costItemId: uuidSchema.optional(),
    reason: z.string().trim().min(1).max(2_000),
});
/**
 * "What would this sale be measured against?" (plan §5.6).
 *
 * The sale rule compares the price against the vehicle priced **as sold** — `isSold = 1`, the days in
 * stock frozen at the sale date — which is a smaller required retail than the live figure the dossier
 * shows, because holding stops on the day the car leaves. The dialog therefore cannot work the answer
 * out for itself without keeping a second copy of the holding rule in the browser; it asks for this
 * instead, and the number it prints is the number `changeStatus` will use.
 */
export const previewVehicleSaleInputSchema = z.object({
    vehicleId: uuidSchema,
    salePrice: amountSchema.positive(),
    saleDate: isoDateSchema,
});
export const vehicleSalePreviewSchema = z.object({
    /** The required retail of the vehicle **priced as sold**; `null` when it cannot be priced at all. */
    requiredRetail: z.number().nullable(),
    belowRetail: z.boolean(),
    /** Below the required retail **and** the caller is not an administrator, as the database has it. */
    adminOnly: z.boolean(),
    /** Below the required retail, so the sale needs a reason whoever records it. */
    noteRequired: z.boolean(),
    /**
     * What the rule refuses on the price and the date alone. The buyer and the reason are typed in the
     * dialog after this is asked for, so their two blockers are reported as the flags above instead.
     */
    blockers: z.array(z.string()),
    /** The day the preview was priced at — the sale date, echoed back. */
    valuationDate: isoDateSchema,
});
/** Distinct values already in use, so the identity form can suggest without inventing a vocabulary. */
export const vehicleAttributeOptionsSchema = z.object({
    makes: z.array(z.string()),
    models: z.array(z.string()),
    colors: z.array(z.string()),
    drivetrains: z.array(z.string()),
    transmissions: z.array(z.string()),
    storageLocations: z.array(z.string()),
    equipment: z.array(z.string()),
});
export const listVehicleActivityInputSchema = paginationInputSchema.extend({
    vehicleId: uuidSchema,
});
export const listVehicleActivityOutputSchema = paginatedSchema(activityEventSchema);
//# sourceMappingURL=vehicles.schemas.js.map